import {
  ArrowLeft,
  ArrowLeftRight,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Columns2,
  Bug,
} from 'lucide-react';

import type { ReaderMode } from '../types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ReaderToolbarProps {
  title: string;
  currentPage: number;
  totalPages: number;
  chapterPosition?: {
    current: number;
    total: number;
  };
  mode: ReaderMode;
  onCycleMode: () => void;
  onBackToGallery: () => void;
  onCopyDebugReport: () => void;
  onPreviousChapter?: () => void;
  onNextChapter?: () => void;
  previousChapterTitle?: string;
  nextChapterTitle?: string;
  className?: string;
}

const MODE_ICON: Record<ReaderMode, typeof Columns2> = {
  vertical: ChevronsUpDown,
  horizontal: ArrowLeftRight,
  spread: Columns2,
};

const MODE_LABEL: Record<ReaderMode, string> = {
  vertical: 'Vertical',
  horizontal: 'Horizontal',
  spread: 'Spread',
};

export function ReaderToolbar({
  title,
  currentPage,
  totalPages,
  chapterPosition,
  mode,
  onCycleMode,
  onBackToGallery,
  onCopyDebugReport,
  onPreviousChapter,
  onNextChapter,
  previousChapterTitle,
  nextChapterTitle,
  className,
}: ReaderToolbarProps) {
  const ModeIcon = MODE_ICON[mode];
  return (
    <header
      className={cn(
        'sticky top-0 z-40 border-b border-border/60 bg-background/95 pt-[var(--app-safe-area-top)] backdrop-blur',
        className,
      )}
    >
      <div className="flex h-12 items-center gap-2 px-2">
        <Button size="icon" variant="ghost" onClick={onBackToGallery} aria-label="Back to gallery">
          <ArrowLeft className="h-4 w-4" />
        </Button>

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm font-medium leading-tight">{title}</h1>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {chapterPosition
              ? `Chapter ${chapterPosition.current} of ${chapterPosition.total} · `
              : ''}
            Page {currentPage} of {totalPages}
          </p>
        </div>

        {(onPreviousChapter || onNextChapter) && (
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={onPreviousChapter}
              disabled={!onPreviousChapter}
              aria-label="Previous chapter"
              title={previousChapterTitle ? `Previous chapter: ${previousChapterTitle}` : 'Previous chapter'}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={onNextChapter}
              disabled={!onNextChapter}
              aria-label="Next chapter"
              title={nextChapterTitle ? `Next chapter: ${nextChapterTitle}` : 'Next chapter'}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        <Button
          size="sm"
          variant="ghost"
          onClick={onCycleMode}
          title={`Mode: ${MODE_LABEL[mode]} (press M)`}
        >
          <ModeIcon className="mr-1.5 h-4 w-4" />
          <span className="hidden sm:inline">{MODE_LABEL[mode]}</span>
        </Button>

        <Button
          size="icon"
          variant="ghost"
          onClick={onCopyDebugReport}
          aria-label="Copy debug report"
          title="Copy debug report"
        >
          <Bug className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}
